import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { emailApi, EmailAccount } from '../../services/emailApi';
import './Composer.css';

const QUILL_TOOLBAR = [
  [{ header: [false, 1, 2, 3] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block'],
  ['link'],
  ['clean'],
];

const QUILL_MODULES = { toolbar: QUILL_TOOLBAR };
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike', 'color', 'background',
  'list', 'indent', 'blockquote', 'code-block', 'link',
];

interface Props {
  accounts: EmailAccount[];
  defaultAccountId?: number;
  mode?: 'compose' | 'reply' | 'forward';
  replyTo?: {
    messageId: number; // внутренний ID для In-Reply-To
    subject?: string;
    from_addr?: string;
    body_text?: string;
    body_html?: string;
  };
  forwardFrom?: {
    subject?: string;
    body_text?: string;
    body_html?: string;
  };
  onClose: () => void;
  onSent?: () => void;
}

interface AttachmentPending {
  filename: string;
  mime_type: string;
  size: number;
  data_b64: string;
  file?: File;  // оригинальный File для multipart
}

const Composer: React.FC<Props> = ({
  accounts,
  defaultAccountId,
  mode = 'compose',
  replyTo,
  forwardFrom,
  onClose,
  onSent,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialSubject =
    mode === 'reply' && replyTo
      ? replyTo.subject?.toLowerCase().startsWith('re:')
        ? replyTo.subject
        : `Re: ${replyTo.subject ?? ''}`
      : mode === 'forward' && forwardFrom
      ? forwardFrom.subject?.toLowerCase().startsWith('fwd:')
        ? forwardFrom.subject
        : `Fwd: ${forwardFrom.subject ?? ''}`
      : '';

  const initialBody = (() => {
    if (mode === 'reply' && replyTo) {
      const quote = (replyTo.body_text || stripHtml(replyTo.body_html || ''))
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
      return `\n\n--- Исходное сообщение от ${replyTo.from_addr || '—'} ---\n${quote}`;
    }
    if (mode === 'forward' && forwardFrom) {
      const body = forwardFrom.body_text || stripHtml(forwardFrom.body_html || '');
      return `\n\n--- Пересылаемое сообщение ---\n${body}`;
    }
    return '';
  })();

  const initialHtml = (() => {
    if (mode === 'reply' && replyTo) {
      const src = replyTo.body_html || (replyTo.body_text || '').replace(/\n/g, '<br>');
      return `<p><br></p><blockquote><p>--- Исходное сообщение от ${replyTo.from_addr || '—'} ---</p>${src}</blockquote>`;
    }
    if (mode === 'forward' && forwardFrom) {
      const src = forwardFrom.body_html || (forwardFrom.body_text || '').replace(/\n/g, '<br>');
      return `<p><br></p><p>--- Пересылаемое сообщение ---</p>${src}`;
    }
    return '';
  })();

  const [accountId, setAccountId] = useState<number>(defaultAccountId ?? accounts[0]?.id);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const [bodyHtml, setBodyHtml] = useState(initialHtml);
  const [richMode, setRichMode] = useState(true);
  const [bodyText, setBodyText] = useState(initialBody);
  const [attachments, setAttachments] = useState<AttachmentPending[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Если reply — to уже знаем
  React.useEffect(() => {
    if (mode === 'reply' && replyTo?.from_addr) {
      setTo(extractEmail(replyTo.from_addr));
    }
  }, [mode, replyTo]);

  const handleAttach = async (files: FileList) => {
    const newAttachments: AttachmentPending[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 20 * 1024 * 1024) {
        setError(`Файл ${file.name} превышает 20 МБ`);
        continue;
      }
      // base64 только для маленьких; для крупных используем multipart
      const useB64 = file.size < 256 * 1024;
      const b64 = useB64 ? await fileToBase64(file) : '';
      newAttachments.push({
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        size: file.size,
        data_b64: b64,
        file,
      });
    }
    setAttachments((cur) => [...cur, ...newAttachments]);
  };

  const handleSend = async () => {
    if (!to.trim()) {
      setError('Укажите получателя');
      return;
    }
    if (!subject.trim()) {
      if (!window.confirm('Тема пуста — отправить?')) return;
    }
    setSending(true);
    setError(null);
    try {
      const bodyTextOut = richMode ? stripHtml(bodyHtml) : bodyText;
      const bodyHtmlOut = richMode ? bodyHtml : undefined;
      // Если есть крупные вложения — используем multipart, иначе base64-JSON
      const hasLarge = attachments.some((a) => !a.data_b64 || a.size > 256 * 1024);
      if (hasLarge && attachments.length > 0) {
        await emailApi.sendMultipart({
          account_id: accountId,
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          body_text: bodyTextOut,
          body_html: bodyHtmlOut,
          in_reply_to_message_id: mode === 'reply' ? replyTo?.messageId : undefined,
          files: attachments.map((a) => a.file!).filter(Boolean),
        });
      } else {
        await emailApi.send({
          account_id: accountId,
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          body_text: bodyTextOut,
          body_html: bodyHtmlOut,
          in_reply_to_message_id: mode === 'reply' ? replyTo?.messageId : undefined,
          attachments_b64: attachments.map((a) => ({
            filename: a.filename,
            mime_type: a.mime_type,
            data_b64: a.data_b64,
          })),
        });
      }
      onSent?.();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="composer-overlay" onClick={onClose}>
      <div className="composer-modal" onClick={(e) => e.stopPropagation()}>
        <div className="composer-header">
          <h3>
            {mode === 'reply' ? '↩️ Ответить' : mode === 'forward' ? '➡️ Переслать' : '✏️ Новое письмо'}
          </h3>
          <button className="composer-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="composer-body">
          <div className="composer-row">
            <label>От:</label>
            <select value={accountId} onChange={(e) => setAccountId(Number(e.target.value))}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name ? `${a.display_name} <${a.email}>` : a.email}
                </option>
              ))}
            </select>
          </div>

          <div className="composer-row">
            <label>Кому:</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="email@example.com, email2@example.com"
            />
            <button className="composer-toggle" onClick={() => setShowCcBcc((v) => !v)}>
              {showCcBcc ? '−' : '+'} Копия / Скрытая
            </button>
          </div>

          {showCcBcc && (
            <>
              <div className="composer-row">
                <label>Копия:</label>
                <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@..." />
              </div>
              <div className="composer-row">
                <label>Скрытая:</label>
                <input
                  type="text"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="bcc@..."
                />
              </div>
            </>
          )}

          <div className="composer-row">
            <label>Тема:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Тема письма"
            />
          </div>

          <div className="composer-mode-switch">
            <button
              type="button"
              className={`composer-mode ${richMode ? 'active' : ''}`}
              onClick={() => setRichMode(true)}
            >
              ✨ Форматирование
            </button>
            <button
              type="button"
              className={`composer-mode ${!richMode ? 'active' : ''}`}
              onClick={() => setRichMode(false)}
            >
              ✏️ Plain text
            </button>
          </div>

          {richMode ? (
            <ReactQuill
              theme="snow"
              value={bodyHtml}
              onChange={setBodyHtml}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Текст сообщения..."
              style={{ marginTop: 6, height: 280, marginBottom: 50 }}
            />
          ) : (
            <textarea
              className="composer-textarea"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Текст сообщения..."
              rows={14}
            />
          )}

          {/* Attachments list */}
          {attachments.length > 0 && (
            <div className="composer-attachments">
              {attachments.map((a, i) => (
                <span key={i} className="composer-attachment">
                  📎 {a.filename} <small>({Math.ceil(a.size / 1024)} KB)</small>
                  <button
                    onClick={() => setAttachments((cur) => cur.filter((_, idx) => idx !== i))}
                    title="Убрать"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}

          {error && <div className="composer-error">⚠️ {error}</div>}
        </div>

        <div className="composer-footer">
          <button
            className="btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
          >
            📎 Вложить
          </button>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            multiple
            onChange={(e) => e.target.files && handleAttach(e.target.files)}
          />
          <button
            className="btn-secondary"
            onClick={async () => {
              try {
                await emailApi.saveDraft({
                  account_id: accountId,
                  to,
                  cc: cc || undefined,
                  bcc: bcc || undefined,
                  subject,
                  body_text: richMode ? stripHtml(bodyHtml) : bodyText,
                  body_html: richMode ? bodyHtml : undefined,
                });
                alert('✅ Черновик сохранён в папке Drafts');
              } catch (e: any) {
                alert('❌ Ошибка: ' + (e?.response?.data?.detail || String(e)));
              }
            }}
            disabled={sending}
          >
            📝 В черновики
          </button>
          <button className="btn-secondary" onClick={onClose} disabled={sending}>
            Отмена
          </button>
          <button className="btn-primary" onClick={handleSend} disabled={sending}>
            {sending ? '⏳ Отправка...' : '📨 Отправить'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── helpers ────────────────────────────────────────────────────

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result = "data:mime;base64,XXXX" — берём только XXXX
      const b64 = result.split(',')[1] || '';
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function extractEmail(addr: string): string {
  // "Name <email@x.y>" → "email@x.y"
  const m = addr.match(/<([^>]+)>/);
  return m ? m[1] : addr.trim();
}

function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default Composer;
